import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import React, { memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { manageDisableScrolling, setSettingsMenuState } from '../../../ducks/ui.duck';
import useAppSelector from '../../../util/hooks/useAppSelector';
import SectionAnnualPassAd from './SectionAnnualPassAd';
import SectionApp from './SectionApp';
import SectionCommunity from './SectionCommunity';
import SectionHelp from './SectionHelp';
import SectionLogin from './SectionLogin';
import SectionLogout from './SectionLogout';
import SectionNavigation from './SectionNavigation';
import SectionPassProducts from './SectionPassProducts';
import SectionStoryblokPages from './SectionStoryblokPages';
import SettingsMenu from './SettingsMenu';

const settingsMenuStyle = {
  position: 'fixed', // Position fixed to overlay entire viewport
  top: 0,
  left: 0,
  bgcolor: 'white',
  height: '100vh',
  width: '100vw',
  zIndex: (theme) => theme.zIndex.modal + 1, // Ensure it's above modals
  p: 4,
};

/**
 * MobileMenuComponent renders the mobile navigation menu with options tailored to the user's authentication status.
 * It includes a fullscreen account settings submenu with slide animation.
 *
 * Features:
 * - Greeting and language selector.
 * - Login/logout actions.
 * - Navigation links to profile, bookings, calendar, listings, etc.
 * - Account settings submenu with slide animation.
 * - Community and help sections.
 *
 * Props:
 * - onClose (function): Function to close the menu.
 *
 * Usage:
 * <MobileMenu onClose={handleMenuClose} />
 * @param root0
 * @param root0.onClose
 */
const MobileMenuComponent = ({ onClose }) => {
  const dispatch = useDispatch();

  const settingsMenuState = useAppSelector((state) => state.ui.settingsMenuState);

  useEffect(() => {
    dispatch(manageDisableScrolling('MobileMenu', true));
    return () => {
      dispatch(manageDisableScrolling('MobileMenu', false));
    };
  }, [dispatch]);

  return (
    <Box>
      <Stack pb={18} pt={4} px={0} spacing={2} width="100%">
        <SectionLogin onClose={onClose} />

        <SectionAnnualPassAd onClose={onClose} />

        <SectionNavigation
          onClose={onClose}
          setSettingsMenuOpen={(state) => dispatch(setSettingsMenuState(state))}
        />

        <SectionApp />

        <SectionPassProducts onClose={onClose} />

        <SectionStoryblokPages onClose={onClose} />

        <SectionCommunity onClose={onClose} />

        <SectionHelp onClose={onClose} />

        <SectionLogout onClose={onClose} />
      </Stack>

      <Slide direction="left" in={settingsMenuState}>
        <Box sx={settingsMenuStyle}>
          <SettingsMenu
            onClose={onClose}
            setSettingsMenuOpen={(state) => dispatch(setSettingsMenuState(state))}
            settingsMenuOpen={settingsMenuState}
          />
        </Box>
      </Slide>
    </Box>
  );
};

MobileMenuComponent.displayName = 'MobileMenu';

const MobileMenu = memo(MobileMenuComponent);

export default MobileMenu;
